import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { LedgersService } from 'src/app/share/services/ledgers/ledgers.service';
import { DefaultConfig } from 'src/app/share/configs/plotly.config';

@Component({
    selector: 'app-performance-price-trends',
    templateUrl: './performance-price-trends.component.html',
    styleUrls: ['./performance-price-trends.component.scss'],
    standalone: false
})
export class PerformancePriceTrendsComponent implements OnChanges {

  @Input() selectedRange: any;
  @Input() selectedTyre: any;
  @Input() branchList: any[] = [];
  @Input() isBranchVisible: boolean = false;

  loading: boolean = false;
  errorMessage: string = '';

  subfilterOptions: any[] = [];
  selectedSubfilter: string = '';

  selectedBranch: string = '';

  public graph: any = {
    data: [],
    layout: {},
    config: {}
  };

  trendsList: any[] = [];
  maxValue: number = 0;

  constructor(private readonly ledgersService: LedgersService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRange']?.currentValue) {
      if (this.selectedRange) {
        this.setdropdownOptions();
      }
    }
  }

  setdropdownOptions() {
    if (this.selectedRange?.period == 7) {
      this.subfilterOptions = [
        { label: '7 Days', value: 'day' }
      ];
      this.selectedSubfilter = 'day';
    } else {
      this.subfilterOptions = [
        { label: 'Weekly', value: 'week' },
        { label: 'Monthly', value: 'month' }
      ];
      this.selectedSubfilter = this.selectedRange?.period == 30 ? 'week' : 'month';
    }
    this.getData();
  }

  getData() {
    if (!this.selectedTyre) return;

    let payload: any = {
      tyreSize: this.selectedTyre.tyreSize,
      tyreMake: this.selectedTyre.tyreMake,
      tyreModel: this.selectedTyre.tyreModel,
      created_datefrom: this.selectedRange.startDate,
      created_dateupto: this.selectedRange.endDate,
      period: this.selectedSubfilter
    };
    if (this.selectedBranch) {
      payload.branch = this.selectedBranch;
    }
    this.getPriceTrendsData(payload);
  }

  onSubfilterChange(event: any) {
    this.selectedSubfilter = event.target.value;
    this.getData();
  }

  onBranchChange(event: any) {
    this.selectedBranch = event.target.value;
    this.getData();
  }

  getPriceTrendsData(payload: any) {
    this.loading = true;
    this.errorMessage = '';
    this.ledgersService.getPriceTrends(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.data) {
          this.initGraph(res.data);
        } else {
          this.errorMessage = "No data available for the selected period.";
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.errorMessage = error?.error?.message || "Failed to fetch price trends";
      }
    });
  }

  initGraph(apiData: any) {
    const originalLabels = apiData.labels || [];
    const customerPrices = apiData.avgCustomerPrice || [];
    const dealerPrices = apiData.avgDealerPrice || [];
    const purchasePrices = apiData.avgPurchasePrice || [];

    const labels = originalLabels.map((label: string, index: number) => {
      return label + '\u200b'.repeat(index);
    });

    this.graph.data = [
      {
        x: labels,
        y: customerPrices,
        name: 'Average Selling Price (Customer)',
        mode: 'lines+markers',
        line: { color: '#00305C', width: 2 },
        marker: { size: 6, color: '#00305C' },
        hovertemplate: 'Avg Selling Price (Customer): ₹%{y:,.0f}<extra></extra>',
      },
      {
        x: labels,
        y: dealerPrices,
        name: 'Average Selling Price (Dealer)',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#EA580C', width: 2 },
        marker: { size: 6, color: '#EA580C' },
        hovertemplate: 'Avg Selling Price (Dealer): ₹%{y:,.0f}<extra></extra>',
      },
      {
        x: labels,
        y: purchasePrices,
        name: 'Average Purchasing Price',
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: '#7C3AED', width: 2 },
        marker: { size: 6, color: '#7C3AED' },
        hovertemplate: 'Avg Purchasing Price: ₹%{y:,.0f}<extra></extra>',
      }
    ];

    this.graph.layout = {
      autosize: true,
      height: 280,
      xaxis: {
        type: 'category',
        showgrid: false,
        zeroline: false,
        tickfont: { color: '#1e293b', size: 10, family: 'Arial, sans-serif' },
        automargin: true,
        showticklabels: true,
        tickangle: originalLabels.length > 10 ? -45 : 0,
        fixedrange: true,
      },
      yaxis: {
        showgrid: true,
        gridcolor: '#f1f5f9',
        zeroline: true,
        zerolinecolor: '#e2e8f0',
        tickfont: { color: '#64748b', size: 10 },
        showticklabels: false,
        rangemode: 'tozero',
        fixedrange: true,
      },
      margin: { l: 25, r: 25, t: 30, b: 50 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: '#ffffff',
        font: { family: 'Arial', size: 12 },
        bordercolor: '#e2e8f0',
        namelength: -1
      },
      showlegend: true,
      legend: {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: 1.15,
        font: { size: 10, color: '#64748b' },
        bgcolor: 'rgba(0,0,0,0)',
      }
    };

    this.graph.config = {
      ...DefaultConfig,
    };

    this.trendsList = originalLabels.map((lbl: any, idx: number) => {
      const price = customerPrices[idx] || 0;
      return {
        label: lbl,
        value: price,
        formattedValue: this.formatNumber(price)
      };
    });
    this.maxValue = Math.max(...this.trendsList.map(item => item.value), 0);
    if (this.maxValue === 0) this.maxValue = 1;
  }

  formatNumber(val: number): string {
    if (!val) return '₹0';
    let formatted = val;
    let suffix = '';
    if (val >= 1000000) {
      formatted = val / 1000000;
      suffix = 'm';
    } else if (val >= 1000) {
      formatted = val / 1000;
      suffix = 'k';
    }
    const result = Math.round(formatted * 10) / 10;
    return `₹${result}${suffix}`;
  }
}
